import React, { useEffect, useState } from "react";
import { Box, Tooltip } from "@mui/material";

const ConnectionGauge = ({ size = 60, checks = 5 }) => {
  const [value, setValue] = useState(0);
  const [latency, setLatency] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    const checkConnection = async () => {
      const start = performance.now();

      try {
        await fetch(
          `https://www.cloudflare.com/cdn-cgi/trace?t=${Date.now()}`,
          {
            mode: "no-cors",
            cache: "no-store",
          },
        );

        const ms = Math.round(performance.now() - start);
        setLatency(ms);

        const strength = Math.max(0, Math.min(100, 100 - (ms / 500) * 100));
        setValue(strength);

        setHistory((previous) => {
          return [...previous, ms].slice(-checks);
        });
      } catch {
        setLatency(null);
        setValue(0);
      }
    };

    checkConnection();
    const interval = setInterval(checkConnection, 5000);

    return () => clearInterval(interval);
  }, [checks]);

  const averageLatency =
    history.length > 0
      ? history.reduce((sum, value) => sum + value, 0) / history.length
      : null;

  const getStatus = () => {
    if (averageLatency === null) {
      return {
        label: "Checking",
        color: "#6b7280",
      };
    }

    if (averageLatency < 100) {
      return {
        label: "Fast Internet",
        color: "#16a34a",
      };
    }

    if (averageLatency < 200) {
      return {
        label: "Good Internet",
        color: "#ca8a04",
      };
    }

    if (averageLatency < 350) {
      return {
        label: "Slow Internet",
        color: "#a16207",
      };
    }

    return {
      label: "Poor Internet",
      color: "#dc2626",
    };
  };

  const status = getStatus();

  const center = 50;
  const radius = 37;

  const startAngle = -135;
  const endAngle = 135;

  const polar = (angle, r = radius) => {
    const radians = ((angle - 90) * Math.PI) / 180;

    return {
      x: center + r * Math.cos(radians),
      y: center + r * Math.sin(radians),
    };
  };

  const getPath = (start, end, r = radius) => {
    const startPoint = polar(end, r);
    const endPoint = polar(start, r);

    return `
      M ${startPoint.x} ${startPoint.y}
      A ${r} ${r} 0 0 0 ${endPoint.x} ${endPoint.y}
    `;
  };

  const segments = 10;
  const segmentGap = 3;
  const needleAngle = startAngle + ((endAngle - startAngle) * value) / 100;
  const needle = polar(needleAngle, 29);

  const getSegmentColor = (index) => {
    // Poor
    if (index < 3) {
      return "#ef4444";
    }

    // Slow
    if (index < 5) {
      return "#a16207";
    }

    // Good
    if (index < 7) {
      return "#facc15";
    }

    // Fast
    return "#22c55e";
  };

  return (
    <Tooltip
      title={
        averageLatency !== null
          ? `${status.label} • Average ${Math.round(averageLatency)} ms`
          : "Checking internet connection..."
      }
      arrow
    >
      <Box
        sx={{
          width: size,
          position: "relative",
          textAlign: "center",
          cursor: "default",
        }}
      >
        <svg
          width={size}
          height={size}
          viewBox="0 0 100 100"
          style={{
            display: "block",
          }}
        >
          {Array.from({ length: segments }).map((_, index) => {
            const segmentSize = (endAngle - startAngle) / segments;

            const start = startAngle + index * segmentSize + segmentGap / 2;

            const end = startAngle + (index + 1) * segmentSize - segmentGap / 2;

            return (
              <path
                key={index}
                d={getPath(start, end)}
                fill="none"
                stroke={getSegmentColor(index)}
                strokeWidth="10"
                strokeLinecap="butt"
              />
            );
          })}

          <line
            x1={center}
            y1={center}
            x2={needle.x}
            y2={needle.y}
            stroke="#111111"
            strokeWidth="3"
            strokeLinecap="round"
            style={{
              transition: "all 400ms ease",
            }}
          />

          <circle
            cx={center}
            cy={center}
            r="6"
            fill="#ffffff"
            stroke="#111111"
            strokeWidth="3"
          />
        </svg>

        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
            fontSize: "10px",
            lineHeight: 1,
            fontWeight: 700,
            color: status.color,
            letterSpacing: "-0.1px",
            whiteSpace: "nowrap",
            mt: -0.7,
          }}
        >
          {status.label}
        </Box>
      </Box>
    </Tooltip>
  );
};

export default ConnectionGauge;
